import React, { useEffect } from 'react';
import './DataProtection.css';

const DataProtection = () => {
    useEffect(() => {
        document.title = 'Data Protection | Avenis Global';
    }, []);

    return (
        <div className="data-protection-page">
            <div className="protection-hero">
                <div className="container">
                    <h1>Data Protection & Security</h1>
                    <p>
                        Enterprise-grade security measures to protect your data
                        and ensure compliance with international standards.
                    </p>
                </div>
            </div>

            <div className="container">
                <div className="protection-content">
                    <div className="protection-overview">
                        <h2>Our Security Commitment</h2>
                        <p>
                            At{' '}
                            <span className="brand-gradient">
                                Avenis Global
                            </span>
                            , we understand that data protection is critical to
                            your business success. We implement enterprise-grade
                            security measures and maintain compliance with
                            international data protection standards to ensure
                            your information is always secure.
                        </p>
                    </div>

                    <div className="security-features">
                        <h2>Security Features</h2>
                        <div className="features-grid">
                            <div className="feature-card">
                                <div className="feature-card-bg"></div>
                                <div className="feature-icon feature-icon-1">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <rect
                                            x="3"
                                            y="11"
                                            width="18"
                                            height="11"
                                            rx="2"
                                            ry="2"
                                        ></rect>
                                        <circle cx="12" cy="16" r="1"></circle>
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                    </svg>
                                </div>
                                <h3>End-to-End Encryption</h3>
                                <p>
                                    All data is encrypted in transit and at rest
                                    using industry-standard AES-256 encryption
                                    protocols.
                                </p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-card-bg"></div>
                                <div className="feature-icon feature-icon-2">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M9 12l2 2 4-4"></path>
                                        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"></path>
                                        <path d="M12 1v6m0 6v6"></path>
                                        <path d="M17.5 6.5l-4.5 4.5-4.5-4.5"></path>
                                    </svg>
                                </div>
                                <h3>Multi-Factor Authentication</h3>
                                <p>
                                    Advanced authentication systems with MFA to
                                    prevent unauthorized access to your
                                    accounts.
                                </p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-card-bg"></div>
                                <div className="feature-icon feature-icon-3">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle cx="12" cy="12" r="3"></circle>
                                        <path d="M12 1v6m0 6v6"></path>
                                        <path d="M21 12h-6m-6 0H3"></path>
                                        <path d="M19.07 4.93l-4.24 4.24m-4.24 4.24L6.35 6.35"></path>
                                        <path d="M4.93 19.07l4.24-4.24m4.24-4.24L17.65 17.65"></path>
                                    </svg>
                                </div>
                                <h3>Continuous Monitoring</h3>
                                <p>
                                    24/7 security monitoring and threat
                                    detection to identify and respond to
                                    potential security issues.
                                </p>
                            </div>
                            <div className="feature-card">
                                <div className="feature-card-bg"></div>
                                <div className="feature-icon feature-icon-4">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="24"
                                        height="24"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                        <circle cx="9" cy="7" r="4"></circle>
                                        <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                    </svg>
                                </div>
                                <h3>Access Controls</h3>
                                <p>
                                    Role-based access controls and audit trails
                                    to ensure only authorized personnel can
                                    access data.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="compliance-standards">
                        <h2>Compliance Standards</h2>
                        <div className="standards-grid">
                            <div className="standard-item">
                                <h3>GDPR Compliance</h3>
                                <p>
                                    Full compliance with the General Data
                                    Protection Regulation (GDPR) for EU data
                                    subjects.
                                </p>
                                <ul>
                                    <li>Data subject rights implementation</li>
                                    <li>Privacy by design principles</li>
                                    <li>Data protection impact assessments</li>
                                    <li>Breach notification procedures</li>
                                </ul>
                            </div>
                            <div className="standard-item">
                                <h3>ISO 27001</h3>
                                <p>
                                    Information security management system
                                    certified to ISO 27001 standards.
                                </p>
                                <ul>
                                    <li>Risk assessment and management</li>
                                    <li>Security policy implementation</li>
                                    <li>Regular security audits</li>
                                    <li>Continuous improvement processes</li>
                                </ul>
                            </div>
                            <div className="standard-item">
                                <h3>SOC 2 Type II</h3>
                                <p>
                                    SOC 2 Type II certification ensuring
                                    security, availability, and confidentiality.
                                </p>
                                <ul>
                                    <li>Security controls implementation</li>
                                    <li>Availability monitoring</li>
                                    <li>Confidentiality protection</li>
                                    <li>Regular compliance audits</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="data-handling">
                        <h2>Data Handling Practices</h2>
                        <div className="handling-sections">
                            <div className="handling-section">
                                <h3>Data Collection</h3>
                                <p>
                                    We only collect data that is necessary for
                                    providing our services and with explicit
                                    consent from data subjects.
                                </p>
                            </div>
                            <div className="handling-section">
                                <h3>Data Processing</h3>
                                <p>
                                    All data processing activities are conducted
                                    in accordance with applicable laws and with
                                    appropriate technical safeguards.
                                </p>
                            </div>
                            <div className="handling-section">
                                <h3>Data Storage</h3>
                                <p>
                                    Data is stored in secure, geographically
                                    distributed data centers with redundant
                                    backup systems.
                                </p>
                            </div>
                            <div className="handling-section">
                                <h3>Data Retention</h3>
                                <p>
                                    We retain data only for as long as necessary
                                    and securely delete it when no longer
                                    needed.
                                </p>
                            </div>
                        </div>
                    </div>
                    <br></br>
                    <div className="contact-security">
                        <div className="security-cta-content">
                            <h2 className="security-cta-title">
                                Need Security Support?
                            </h2>
                            <p className="security-cta-description">
                                For security-related inquiries or to report a
                                security incident, our compliance team is ready
                                to assist you.
                            </p>
                            <div className="security-contact">
                                <a
                                    href="mailto:compliance@avenisglobal.com"
                                    className="security-email-link"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="20"
                                        height="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                        <polyline points="22,6 12,13 2,6"></polyline>
                                    </svg>
                                    compliance@avenisglobal.com
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DataProtection;
